import React, {useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Pie, Doughnut} from 'react-chartjs-2';
import t from '../../helpers/t';

const getSuitableY = (y, yArray = [], direction) => {
  let result = y;
  yArray.forEach((existedY) => {
    if (existedY - 14 < result && existedY + 14 > result) {
      if (direction === 'right') {
        result = existedY + 14;
      } else {
        result = existedY - 14;
      }
    }
  });
  return result;
};

const getOriginPoints = (source, center, l) => {
  let a = {x: 0, y: 0};
  var dx = (center.x - source.x) / l;
  var dy = (center.y - source.y) / l;
  a.x = center.x + l * dx;
  a.y = center.y + l * dy;
  return a;
};

const getOrCreateLegendList = (chart, legendContainer) => {
  if (!legendContainer) return;

  let listContainer = legendContainer.querySelector('ul');

  if (!listContainer) {
    listContainer = document.createElement('ul');
    listContainer.style.display = 'flex';
    listContainer.style.flexDirection = 'row';
    listContainer.style.margin = 0;
    listContainer.style.padding = 0;

    legendContainer.appendChild(listContainer);
  }

  return listContainer;
};

const htmlLegendPlugin = {
  id: 'htmlLegend',
  afterUpdate(chart, args, options) {
    const ul = getOrCreateLegendList(chart, args.container);
    if (!ul) return;

    // Remove old legend items
    while (ul.firstChild) {
      ul.firstChild.remove();
    }
    ul.style.flexWrap = 'wrap';
    ul.style.justifyContent = 'center';
    ul.style.maxHeight = '34px';
    ul.style.overflowY = 'auto';

    if (!chart.options?.legend?.labels?.hasOwnProperty('generateLabels'))
      return;

    // // Reuse the built-in legendItems generator
    const items = chart.options.legend.labels.generateLabels(chart);

    items.forEach((item) => {
      const li = document.createElement('li');
      li.style.alignItems = 'center';
      li.style.cursor = 'pointer';
      li.style.display = 'flex';
      li.style.flexDirection = 'row';
      li.style.marginLeft = '10px';

      li.onclick = () => {
        const {type} = chart.config;
        if (type === 'pie' || type === 'doughnut') {
          // Pie and doughnut charts only have a single dataset and visibility is per item
          const index = item.index;
          const meta = chart.getDatasetMeta(0);
          const legend = meta.data[index];
          const alreadyHidden = legend.hidden === null ? false : legend.hidden;
          meta.data[index].hidden = !alreadyHidden;
        }
        chart.update();
      };

      // Color box
      const boxSpan = document.createElement('span');
      boxSpan.style.background = item.fillStyle;
      boxSpan.style.borderColor = item.strokeStyle;
      boxSpan.style.borderWidth = item.lineWidth + 'px';
      boxSpan.style.display = 'inline-block';
      boxSpan.style.height = '10px';
      boxSpan.style.marginRight = '2px';
      boxSpan.style.width = '10px';

      // Text
      const textContainer = document.createElement('p');
      textContainer.style.color = item.fontColor;
      textContainer.style.margin = 0;
      textContainer.style.padding = 0;
      textContainer.style.font =
        "normal 12px 'Open Sans', 'Helvetica', 'Arial', sans-serif";
      //textContainer.style.textDecoration = item.hidden ? 'line-through' : '';

      const text = document.createTextNode(item.text);
      textContainer.appendChild(text);

      li.appendChild(boxSpan);
      li.appendChild(textContainer);
      li.style.opacity = item.hidden ? '50%' : '100%';
      ul.appendChild(li);
    });
  },
};

function textInsideDoughnutPlugin(value = 0) {
  return {
    id: 'text',
    beforeDraw: function (chart, a, b) {
      var width = chart.width,
        height = chart.height,
        ctx = chart.ctx;

      ctx.restore();
      ctx.font = "normal 12px 'Open Sans', 'Helvetica', 'Arial', sans-serif";
      ctx.textBaseline = 'middle';

      var text = `${value.toFixed(2)} kg CO2`,
        textX = Math.round((width - ctx.measureText(text).width) / 2),
        textY = height / 2 + 5;

      const textBefore = t('saved');
      ctx.fillText(
        textBefore,
        Math.round((width - ctx.measureText(textBefore).width) / 2),
        textY - 13,
      );
      ctx.fillText(text, textX, textY);
      ctx.save();
    },
  };
}

function lableOutsideChartPlugin(type = 'pie') {
  return {
    afterDraw: (chart) => {
      const ctx = chart.ctx;
      ctx.save();
      ctx.font = "10px 'Averta Std CY'";
      const leftLabelCoordinates = [];
      const rightLabelCoordinates = [];
      const chartCenterPoint = {
        x:
          (chart.chartArea.right - chart.chartArea.left) / 2 +
          chart.chartArea.left,
        y:
          (chart.chartArea.bottom - chart.chartArea.top) / 2 +
          chart.chartArea.top,
      };
      chart.config.data.labels.forEach((label, i) => {
        const meta = chart.getDatasetMeta(0);
        const arc = meta.data[i];
        const dataset = chart.config.data.datasets[0];

        const legend = meta.data[i];
        const alreadyHidden = legend.hidden === null ? false : legend.hidden;

        if (alreadyHidden) return;
        // Prepare data to draw
        // important point 1
        const centerPoint = arc.getCenterPoint();
        const model = arc._model;
        let color = model.borderColor;
        let labelColor = model.borderColor;
        if (dataset.polyline && dataset.polyline.color) {
          color = dataset.polyline.color;
        }
        if (dataset.polyline && dataset.polyline.labelColor) {
          labelColor = dataset.polyline.labelColor;
        }

        const angle = Math.atan2(
          centerPoint.y - chartCenterPoint.y,
          centerPoint.x - chartCenterPoint.x,
        );
        // important point 2, this point overlapsed with existed points
        // so we will reduce y by 14 if it's on the right
        // or add by 14 if it's on the left
        let originPoint = getOriginPoints(
          chartCenterPoint,
          centerPoint,
          model.outerRadius,
        );
        const radius = type === 'pie' ? model.outerRadius : model.innerRadius;
        const radiusDiffereceX =
          type === 'pie' ? model.outerRadius + 10 : model.innerRadius + 20;
        const radiusDiffereceY =
          type === 'pie' ? model.outerRadius + 15 : model.innerRadius + 25;
        const point2X =
          chartCenterPoint.x +
          Math.cos(angle) *
            (centerPoint.x < chartCenterPoint.x
              ? radiusDiffereceX
              : radiusDiffereceX);
        let point2Y =
          chartCenterPoint.y +
          Math.sin(angle) *
            (centerPoint.y < chartCenterPoint.y
              ? radiusDiffereceY
              : radiusDiffereceY);

        let suitableY;
        if (point2X < chartCenterPoint.x) {
          // on the left
          suitableY = getSuitableY(point2Y, leftLabelCoordinates, 'left');
        } else {
          // on the right
          suitableY = getSuitableY(point2Y, rightLabelCoordinates, 'right');
        }

        point2Y = suitableY;

        let value = dataset.data[i];
        const sum = dataset.data.reduce((a, b) => a + b, 0);
        if (dataset.polyline && dataset.polyline.formatter) {
          value = dataset.polyline.formatter(
            " " + value + ' - ' + ((value * 100) / sum).toFixed(2) + ' % ',
          );
        }
        let edgePointX =
          point2X < chartCenterPoint.x
            ? chartCenterPoint.x - radius - 10
            : chartCenterPoint.x + radius + 10;

        if (point2X < chartCenterPoint.x) {
          leftLabelCoordinates.push(point2Y);
        } else {
          rightLabelCoordinates.push(point2Y);
        }

        //DRAW CODE
        // first line: connect between arc's center point and outside point
        ctx.lineWidth = 1;
        ctx.strokeStyle = color;
        ctx.beginPath();
        ctx.moveTo(
          type === 'pie' ? originPoint.x : centerPoint.x,
          type === 'pie' ? originPoint.y : centerPoint.y,
        );
        ctx.lineTo(point2X, point2Y);
        ctx.stroke();
        // second line: connect between outside point and chart's edge
        ctx.beginPath();
        ctx.moveTo(point2X, point2Y);
        ctx.lineTo(edgePointX, point2Y);
        ctx.stroke();
        //fill custom label
        const labelAlignStyle =
          edgePointX < chartCenterPoint.x ? 'right' : 'left';
        const labelX =
          edgePointX < chartCenterPoint.x ? edgePointX : edgePointX + 0;
        const labelY = point2Y + 7;
        ctx.textAlign = labelAlignStyle;
        ctx.textBaseline = 'bottom';
        ctx.font = "normal 12px 'Open Sans', 'Helvetica', 'Arial', sans-serif";
        ctx.fillStyle = labelColor;
        ctx.fillText(value, labelX, labelY);
      });
      ctx.restore();
    },
  };
}

const plugins = [htmlLegendPlugin];

const PieChart = ({props}) => {
  const [data, setData] = useState();
  const customLegend = useRef();
  const [plugin, setPLugin] = useState();

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      tooltips: {
        backgroundColor: '#FFF',
        callbacks: {
          labelTextColor: function (_) {
            return '#000000';
          },
          label: function (tooltipItem, data) {
            let label = data.labels[tooltipItem.index];
            return label;
          },
          afterLabel: function (tooltipItem, data) {
            let value =
              data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
            const sum = data.datasets[tooltipItem.datasetIndex].data.reduce(
              (a, b) => a + b,
              0,
            );
            value = value + ' - ' + ((value * 100) / sum).toFixed(2) + ' %';
            return value;
          },
        },
      },
      legend: {
        display: false,
        position: 'bottom',
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          font: {
            size: 12,
            family: "'Open Sans', 'Helvetica', 'Arial', sans-serif",
            weight: 'normal',
          },
        },
        onClick: function (_, legendItem) {
          const index = legendItem.index;
          const chart = this.chart;
          const meta = chart.getDatasetMeta(0);
          const legend = meta.data[index];
          const alreadyHidden = legend.hidden === null ? false : legend.hidden;
          meta.data[index].hidden = !alreadyHidden;

          chart.update();
        },
      },
      plugins: {
        htmlLegend: {
          // ID of the container to put the legend in
          container: customLegend?.current,
        },
        legend: {
          display: false,
        },
      },
      layout: {
        padding: {
          top: 60,
          left: 60,
          right: 60,
          bottom: 60,
        },
      },
    }),
    [customLegend?.current, props?.data],
  );

  useLayoutEffect(() => {
    if (props?.data) {
      const usableData = Object.entries(props?.data?.json[0]).map(
        ([key, val]) => {
          return {label: key, value: val};
        },
      );
      const keys = props?.data?.keys?.value;
      const colors = props?.color?.pattern;
      if (!usableData?.length) return;
      const normData = {
        labels: keys,
        datasets: [
          {
            data: usableData.map((val) => val.value),
            backgroundColor: colors.slice(0, keys?.length || 0),
            polyline: {
              color: 'black',
              labelColor: 'black',
              formatter: (value) => `${value}`,
            },
          },
        ],
      };
      setData(normData);
      if (props.data?.type === 'donut') {
        let complete = 0;
        Object.entries(props?.data?.json[0])?.map((type) => {
          complete += type[1];
        });
        setPLugin([
          ...plugins,
          textInsideDoughnutPlugin(complete),
          lableOutsideChartPlugin(props.data?.type),
        ]);
        return;
      }
      setPLugin([...plugins, lableOutsideChartPlugin('pie')]);
    }

    return () => {
      setData();
    };
  }, [props?.data]);

  return (
    <div className="chart-container">
      {data && props?.data?.type === 'pie' ? (
        <Pie data={data} options={options} plugins={plugin} />
      ) : (
        plugin && (
          <Doughnut
            data={data}
            options={{
              ...options,
              layout: {
                padding: {
                  top: 10,
                  left: 10,
                  right: 10,
                  bottom: 10,
                },
              },
            }}
            plugins={plugin}
          />
        )
      )}
      <div className="chart-legend" ref={customLegend}></div>
    </div>
  );
};

export default PieChart;
